/**
 * useMainApp — the in-gym shell's state, effects and handlers, composed from
 * focused sub-hooks and handed to `MainApp` as one object.
 *
 * That object is the app's widest interface — ~150 names crossing into six
 * tabs — and `src/__tests__/mainAppContract.test.js` checks it in both
 * directions. Anything moved out of here has to stay reachable through the
 * return, and anything the return names has to have a reader.
 *
 * What lives WHERE, and why:
 *
 * - `useAuth`, `useGymData`, `useFriendships`, `useCompetition`, `useSeries`,
 *   `useEvents` — the data sources.
 * - `useMapFilters` — filter / search / sort / floor state. Held here rather
 *   than in `MapTab` because the tabs are conditionally rendered; see that
 *   file's header.
 * - `useRouteMutations`, `useMemberMutations` — the writes.
 * - `useSprayWalls` — the add-on-gated spray wall list (map markers; the
 *   Spraywall tab reuses it — there is no second `sprayWalls` listener).
 * - Still here on purpose: the tab machinery (hash, history, scroll memory,
 *   the Spraywall tab's visibility and its bounce / wait rule, and the
 *   navigation guard a tab can register to veto leaving — the wall editor's
 *   "Elveted az átnézést?"), the route AND spray wall PLACEMENT mode (`pending` — a UI mode entangled
 *   with the tab and the selection, not a write; a wall carries
 *   `kind: "sprayWall"`), climb logging, and the competition-route
 *   visibility rules, which need three data sources at once.
 */
import { useState, useEffect, useMemo, useRef, useCallback, createElement } from "react";
import { useNavigate } from "react-router";
import { db } from "../firebase";
import {
  doc, getDoc, setDoc, updateDoc, writeBatch, deleteField, increment,
  serverTimestamp, arrayRemove, arrayUnion, getDocs, query, where, Timestamp
} from "firebase/firestore";
import { DEFAULT_SETTINGS, DIFFICULTY, HOLD_COLORS, DEFAULT_ACCENT } from "../constants";
import { getMaps, getDifficulty, getHoldColors, todayStr, isFeatureEnabled, getPlanLimits } from "../utils";
// storageUtils dynamically imported in handleUpload to avoid pulling firebase/storage into main bundle
import { useTranslation } from "../i18n";
import { useGym } from "../contexts/GymContext";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import { usePermissions } from "../permissions";
import { useCompetition } from "./useCompetition";
import { useSeries } from "./useSeries";
import { useEvents } from "./useEvents";
import { trackMAU } from "./useMAU";
import { logEvent, EVT } from "../analytics";
import { getRouteDisplayName, sortRoutes } from "../utils/routeDisplayUtils";
import { resolveAscentRoute } from "../utils/ascentRoute";
import { haptic } from "../utils/haptics";
import { CLIMB_KIND, CLIMB_COOLDOWN_MS, buildClimbEntry, climbLogDocId, climbAtMillis, isWithinCooldown, computeClimbTotals, liveDayClimbs, attemptEntryIdentity, planClimbRemoval } from "../utils/climbLog";
import { buildNewRouteDoc } from "../utils/routeDoc";
import { commitPlacement } from "../utils/mapPlacement";
import { attemptsBucket, isGradeOpinion, clampAttempts, sendAttemptCounts } from "../utils/routeLogModel";
import { planSetSendAttempts, planAttemptReduction, planRemoveRouteDay, mapWithoutRouteSends } from "../utils/routeLogWrites";

// Extracted sub-hooks
import { useAuth } from "./useAuth";
import { useGymData } from "./useGymData";
import { usePWA } from "./usePWA";
import { usePullToRefresh } from "./usePullToRefresh";
import { useFriendships } from "./useFriendships";
import { useAppTheme } from "../contexts/ThemeContext";
import { useVisibilityResume } from "./useVisibilityResume";
import { useQuickLog } from "./useQuickLog";
import { useArchiveMode } from "./useArchiveMode";
import { useScrollMemory } from "./useScrollMemory";
import { useMemberMutations } from "./useMemberMutations";
import { useRouteMutations } from "./useRouteMutations";
import { useMapFilters } from "./useMapFilters";
import { getAllDifficulties, getGymDisciplines } from "../utils/disciplineUtils";
import { getSelfScoreState, getAllCompRouteIds, findActiveStandardCompetition, activeStandardCompetitions } from "../competitionUtils";
import { useToast, IconSprayWallColor } from "../components/ui";
import { isAddonActive, isClimbEntry, attemptEntryId, ascentDocId, buildAscentDoc } from "@sendly/shared";
import { useSprayWalls } from "./useSprayWalls";
import { navigateToSprayWall, sprayWallPlacementPatch, SPRAY_WALL_TAB } from "../utils/sprayWallMap";
import { sprayWallTabVisible, sprayWallTabDecision } from "../utils/sprayWallTab";
import { capGatedTabDecision } from "../utils/tabAccess";
import { callableErrorText } from "../utils/callableError";
import { callFunction } from "../utils/callFunction";
import { sessionStore } from "../utils/safeStorage";
import { recordPwaGym } from "../utils/pwaApp";
import { useAdminStatsSources } from "./useAdminStatsSources";
import { useSprayPickerSources } from "./useSprayPickerSources";
import { useCompTabSources } from "./useCompTabSources";
import { recordViewOpen } from "../utils/tabUsage";
import { useRouteLogCache } from "./useRouteLogCache";
import { groupClimbLogsByRoute } from "../utils/routeLogCache";

// The Spraywall tab icon's box: one emoji line tall (the tab bar's icon slot is
// fontSize 16 at lineHeight 1.2), so the label under it lines up with the
// labels under the emoji icons.
const SPRAY_TAB_ICON_BOX = { display: "flex", alignItems: "center", justifyContent: "center", height: "1.2em" };

export function useMainApp({ initialEventId = null } = {}) {
  const { t, lang } = useTranslation();
  // One listener for the whole app (index.js) — this hook only reads it.
  const { userData } = useCurrentUser();
  const navigate = useNavigate();
  const { gymId, gymCollection, gymDoc, gymMeta, gymDocRef, memberState } = useGym();

  // ── Auth & user state ──
  const authHook = useAuth({ gymId, gymDoc, lang });
  const {
    authUser, authLoading, seedName,
    emailInput, setEmailInput, passwordInput, setPasswordInput,
    emailSent, setEmailSent,
    authError, setAuthError, needsEmailConfirm, handleConfirmEmail,
    isStandalone, isIOSStandalone, isIOS,
    isPasswordAuthEnabled,
    handleLogin, handleAppleLogin, handleEmailLink, handleLogout,
    handlePasswordSignIn, handlePasswordSignUp,
  } = authHook;

  // ── Friendships (before gymData so friend UIDs can be passed as extraUids) ──
  const friendshipHook = useFriendships({ authUser });
  const {
    friendships,
    acceptedFriendUids, pendingIncoming, pendingOutgoing,
    handleSendFriendRequestByUid,
  } = friendshipHook;

  // Collect all friend UIDs (accepted + pending) so their user docs are fetched
  const allFriendUids = useMemo(() => {
    const uids = new Set(acceptedFriendUids);
    pendingIncoming.forEach(f => { if (f.requestedBy) uids.add(f.requestedBy); });
    pendingOutgoing.forEach(f => {
      const otherUid = f.users?.find(uid => uid !== authUser?.uid);
      if (otherUid) uids.add(otherUid);
    });
    return [...uids].filter(Boolean);
  }, [acceptedFriendUids, pendingIncoming, pendingOutgoing, authUser?.uid]);

  // ── Gym data (scoped users: gym members + friends) ──
  const gymData = useGymData({ authUser, gymId, gymCollection, extraUids: allFriendUids, timezone: gymMeta?.timezone });
  const { dataLoaded, routes, ascents, allAscents, members, users, ascentCounts, fetchAllAscents, bumpAscentCount, todayClimbLog, todayKey, fetchClimbLogs, myGymStats, rankingAscents, fetchRankingAscents, rankingStatus, routeAscents, fetchAscentsForRoutes, routeRatings, fetchRatingsForRoutes, patchRouteRating, ownMember, isBanned, archivedRoutes, archivedRoutesHasMore, fetchArchivedRoutes, loadMoreArchivedRoutes, fetchAllArchivedRoutes, invalidateArchivedRoutes, fetchRoutesArchivedSince } = gymData;

  // ── The climber's own route entries, read once in the background ──
  // (docs/spec/11-map-routes.md, "Loading the state"): the route modal opens
  // with live buttons instead of waiting for a per-route query.
  const routeLogCache = useRouteLogCache({ uid: authUser?.uid ?? null, gymId, memberActive: memberState === "active", todayKey, routes, todayClimbLog, fetchClimbLogs });

  // ── Resume from background — force Firestore reconnect ──
  useVisibilityResume();

  // ── Settings & derived values ──
  const settings = useMemo(() => ({ ...DEFAULT_SETTINGS, ...(gymMeta?.settings || {}) }), [gymMeta]);
  const accent = gymMeta?.accentColor || DEFAULT_ACCENT;
  // Memoized because the identity of both objects is a memo dependency in
  // several consumers (`sortedRouteList` below, the Routes tab's filter memo,
  // the Setters tab's per-setter aggregate). `getDifficulty` returns the
  // shared defaults object for an uncustomised gym, but builds a fresh one for
  // a customised palette — which is most real gyms, so without this those
  // memos re-ran on every render.
  const diff = useMemo(() => getDifficulty(settings, DIFFICULTY), [settings]);
  // Union of every discipline's set, for the one consumer below that prices
  // ascents across disciplines in a single pass (the header score). The map,
  // filters and route names keep the discipline-scoped `diff`.
  const diffAll = useMemo(() => getAllDifficulties(settings, getGymDisciplines(gymMeta), DIFFICULTY), [settings, gymMeta]);
  const holdC = useMemo(() => getHoldColors(settings, HOLD_COLORS), [settings]);

  // ── Map filters, search, sort and floor ──
  const {
    filters, setFilters, toggleDiff, activeFilterCount,
    disciplineFilter, setDisciplineFilter,
    mapSearch, setMapSearch, mapSort, setMapSort,
    showMapFilters, setShowMapFilters,
    activeMapId, setActiveMapId, maps, currentMapId, currentMap,
    hasUngradedRoutes, hasUnsetFieldRoutes,
  } = useMapFilters({ routes, diff, settings, gymMeta });
  const { setUserTheme, th } = useAppTheme();

  // ── Spray walls (map markers; the Spraywall tab reuses this list) ──
  // Add-on gated per the performance guide: a gym without the add-on never
  // subscribes to `sprayWalls`.
  const sprayWallAddonOn = isAddonActive(gymMeta, "sprayWall");
  const { sprayWalls, sprayWallsLoaded } = useSprayWalls({ gymId, gymCollection, enabled: sprayWallAddonOn });

  // Sync Firestore theme → localStorage (fixes PWA having separate localStorage)
  const themeSyncedRef = useRef(false);
  useEffect(() => {
    if (!themeSyncedRef.current && userData?.theme) {
      themeSyncedRef.current = true;
      setUserTheme(userData.theme);
    }
  }, [userData?.theme, setUserTheme]);

  // ── UI state ── (tab synced with URL hash for back-button support)
  const validTabKeys = useRef(new Set(["map", SPRAY_WALL_TAB, "stats", "comp", "routes", "setters", "admin"]));
  const getTabFromHash = useCallback(() => {
    const h = window.location.hash.replace("#", "");
    // Support sub-paths like "admin/community" — the sub-tab only matters to
    // the tab component itself; the main tab switcher only cares about the
    // first segment.
    const baseTab = h.split("/")[0];
    return validTabKeys.current.has(baseTab) ? baseTab : "map";
  }, []);
  const [tab, setTabRaw] = useState(() => getTabFromHash());
  const tabRef = useRef(tab);

  // Every tab scrolls the same window, so without this the offset carried over
  // between them: the long Utak tab left the short Építők tab parked past its
  // own end (a screenful of bare background on mobile). Each tab now keeps its
  // own offset, remembered per gym; one you have never scrolled opens at the
  // top. Disabled until the gym is known so the offsets are never filed under
  // the wrong gym.
  const scrollRestoring = useScrollMemory(gymId ? tab : null, gymId);

  // The device's usage memory: which tabs it opened on this gym lately orders
  // the warm-up and picks what the background prefetch reads
  // (docs/spec/01f-tab-loading.md, "Background data prefetch by usage").
  useEffect(() => { if (gymId && tab) recordViewOpen(gymId, tab); }, [gymId, tab]);
  // In a gym's own installed app, remember that gym's id for the push token
  // write (utils/pwaApp.js) — the server titles that app's pushes by category.
  useEffect(() => { recordPwaGym(gymMeta); }, [gymMeta]);

  // Wrap setTab to push browser history entries. We also synthesize a
  // `hashchange` event after `pushState` — `pushState` itself is silent
  // (no popstate, no hashchange), so any external listener that wants
  // to react to in-app tab navigation has nothing to hook into. The
  // App.js PWA-restore persist effect needs this so the saved
  // "last gym URL" reflects the active tab when the user closes the
  // standalone PWA. (AdminPanel.setActiveSection already does the same.)
  const switchTab = useCallback((newTab) => {
    if (newTab === tabRef.current) return;
    tabRef.current = newTab;
    setTabRaw(newTab);
    logEvent(EVT.TAB_VIEW, { gym_id: gymId, tab: newTab });
    const newHash = newTab === "map" ? "" : "#" + newTab;
    const url = window.location.pathname + window.location.search + newHash;
    window.history.pushState(null, "", url);
    try { window.dispatchEvent(new HashChangeEvent("hashchange")); } catch {}
  }, [gymId]);

  // ── Navigation guard ──
  // One slot: a tab with unsaved work (the Spraywall wall editor during a hold
  // review) registers `fn: () => Promise<boolean>` (true = may leave) and gets
  // an unregister back. Tab switches and Back / Forward / deep links that
  // change the base tab ask it first (spec 21c, "Navigation and hash
  // routing"). A second prompt while one is open is dropped: a fragment
  // traversal fires popstate AND hashchange, and both would ask.
  const navGuardRef = useRef(null);
  const guardPendingRef = useRef(false);
  // The last URL the current tab showed — where "stay" puts the address bar
  // back after a Back press that already moved it.
  const lastTabUrlRef = useRef(typeof window !== "undefined"
    ? window.location.pathname + window.location.search + window.location.hash
    : "");
  const registerNavigationGuard = useCallback((fn) => {
    navGuardRef.current = typeof fn === "function" ? fn : null;
    return () => { if (navGuardRef.current === fn) navGuardRef.current = null; };
  }, []);
  // Ask the guard, then run `proceed` (leave) or put `revertUrl` back (stay).
  // Resolves to the answer; a throwing guard counts as "stay".
  const askNavigationGuard = useCallback((proceed, revertUrl) => {
    const guard = navGuardRef.current;
    if (guardPendingRef.current) return Promise.resolve(false);
    guardPendingRef.current = true;
    return Promise.resolve()
      .then(() => guard())
      .then((ok) => ok === true, () => false)
      .then((ok) => {
        guardPendingRef.current = false;
        if (ok) proceed();
        else if (revertUrl) {
          window.history.pushState(null, "", revertUrl);
          try { window.dispatchEvent(new HashChangeEvent("hashchange")); } catch {}
        }
        return ok;
      });
  }, []);

  // Returns a Promise<boolean> only when a guard was asked (so a caller such as
  // the tab bar can drop its optimistic highlight on "stay").
  const setTab = useCallback((newTab) => {
    if (newTab === tabRef.current) return undefined;
    if (!navGuardRef.current) { switchTab(newTab); return undefined; }
    return askNavigationGuard(() => switchTab(newTab), null);
  }, [switchTab, askNavigationGuard]);

  // A tab the viewer may not see (features off, add-on off, capability missing)
  // goes back to the map WITHOUT a history entry: the bounce REPLACES the URL
  // that named the tab. A pushed bounce left that URL behind it, so Back opened
  // the hidden tab again, which bounced forward again — the viewer could never
  // go back past it. No navigation guard (the tab is gone either way) and no
  // `tab_view` (the viewer did not choose the map).
  const bounceToMap = useCallback(() => {
    if (tabRef.current === "map") return;
    tabRef.current = "map";
    setTabRaw("map");
    try {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    } catch { /* history unavailable (sandboxed frame): the tab state alone moved */ }
  }, []);

  // Opens the Spraywall tab at one wall (marker tap, post-placement return).
  // One history entry (D91): setTab pushes `#spraywall`, the wall id is then
  // written with replaceState. TAB_VIEW therefore logs only 'spraywall'.
  const openSprayWall = useCallback((wallId, sub) => navigateToSprayWall(setTab, wallId, sub), [setTab]);

  const [selRoute, setSelRoute] = useState(null);
  const [selRouteEditMode, setSelRouteEditMode] = useState(false);
  const [leaderboardDiscipline, setLeaderboardDiscipline] = useState("boulder");
  const [showBuilder, setShowBuilder] = useState(false);
  const [pending, setPending] = useState(null);
  // The Routes tab's setter filter and the Stats tab's friends toggle. They
  // sit up here for the same reason the map filters do — the tabs are
  // conditionally rendered, so state owned by a tab dies when you leave it.
  const [setterSearch, setSetterSearch] = useState("");
  // showLegend (persisted) and the route list's visible count (session-only) live in MapTab
  const [friendsFilter, setFriendsFilter] = useState(false);
  const [showGymInfo, setShowGymInfo] = useState(false);

  // Legacy `?section=…` deep links (friend-request pushes and recap
  // unsubscribe links already delivered before settings moved to /profile).
  // Redirect them to the matching profile settings section.
  //  - recap   → email-recap unsubscribe link
  //  - friends → friend-request / accepted push
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const section = params.get("section");
    if (section !== "recap" && section !== "friends") return;
    window.location.assign(section === "recap" ? "/profile#settings/emails" : "/profile#settings/friends");
  }, []);

  // Listen for popstate (browser back/forward) to update tab
  useEffect(() => {
    const currentUrl = () => window.location.pathname + window.location.search + window.location.hash;
    const onPopState = () => {
      const h = window.location.hash.replace("#", "");
      // Allow sub-paths like "admin/community" — strip the trailing segment
      // when looking up the top-level tab so deep links still route correctly.
      const baseTab = h.split("/")[0];
      let next = null;
      if (validTabKeys.current.has(baseTab)) {
        next = baseTab;
      } else if (!window.location.hash || window.location.hash === "#") {
        // No hash — could be initial "map" state or navigated fully back
        // If we're still on the gym page, show map tab
        if (window.location.pathname.startsWith("/gym/")) next = "map";
      }
      const apply = () => {
        if (next) {
          tabRef.current = next;
          setTabRaw(next);
        }
        lastTabUrlRef.current = currentUrl();
        // Cancel pending operations when navigating back
        setPending(null);
        setSelRoute(null);
        setShowBuilder(false);
      };
      // Leaving the tab while it guards unsaved work: the URL has already
      // moved, so "stay" pushes the last in-tab URL back.
      if (next && next !== tabRef.current && navGuardRef.current) {
        askNavigationGuard(apply, lastTabUrlRef.current);
        return;
      }
      apply();
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [askNavigationGuard]);

  // Listen for hashchange to update the tab. `popstate` alone is not enough:
  // a fragment navigation that creates a NEW history entry fires `hashchange`
  // and never `popstate`. That's exactly what the service worker does when a
  // push notification deep-links an ALREADY-OPEN window (`client.navigate()`
  // in sw.js `notificationclick`) — so tapping "new routes" used to focus the
  // PWA and leave whatever tab was showing before.
  //
  // In-app navigation (`setTab` above, `AdminPanel.setActiveSection`) also
  // dispatches a synthetic `hashchange`, but both set the tab first, so the
  // equality check below turns those into no-ops.
  useEffect(() => {
    const currentUrl = () => window.location.pathname + window.location.search + window.location.hash;
    const onHashChange = () => {
      if (!window.location.pathname.startsWith("/gym/")) return;
      const baseTab = window.location.hash.replace("#", "").split("/")[0];
      const next = validTabKeys.current.has(baseTab) ? baseTab : "map";
      if (next === tabRef.current) {
        // In-tab navigation (or the echo of setTab): remember it as the place
        // a vetoed Back returns to.
        lastTabUrlRef.current = currentUrl();
        return;
      }
      const apply = () => {
        tabRef.current = next;
        setTabRaw(next);
        lastTabUrlRef.current = currentUrl();
      };
      if (navGuardRef.current) {
        askNavigationGuard(apply, lastTabUrlRef.current);
        return;
      }
      apply();
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [askNavigationGuard]);

  // ── Route writes (edit / delete / rate / bulk) ──
  const {
    handleEditRoute, handleDeleteRoute, setRouteActive,
    handleRate: writeRating, handleBulkArchive, handleBulkDelete,
  } = useRouteMutations({ gymId, gymDoc, authUser, gymMeta, invalidateArchivedRoutes });

  // The write, then the cached comment list of that route: once it succeeded
  // the viewer's own rating is patched into `routeRatings` (a route nobody
  // asked for is left alone — its first read includes the write), so the list
  // a viewer just edited is current without a re-read. Rejections (profanity,
  // rate limit, save error) return without touching the cache.
  const handleRate = useCallback(async (routeId, stars, comment) => {
    const result = await writeRating(routeId, stars, comment);
    const uid = authUser?.uid;
    if (result?.success && uid) {
      const starCount = Number(stars) > 0 ? Number(stars) : 0;
      const text = comment && comment.trim() ? comment : "";
      patchRouteRating(
        routeId,
        starCount > 0 || text ? { id: uid + "_" + routeId, userId: uid, routeId, stars: starCount, comment: text } : null,
        uid,
      );
    }
    return result;
  }, [writeRating, patchRouteRating, authUser?.uid]);

  // ── Member management (admin actions on a gym member) ──
  const { handleRole, handleKick, handleBan, handleUnban, handleReportName } = useMemberMutations(gymId);

  // ── PWA ──
  const pwaHook = usePWA({ gymMeta, isStandalone });
  const { installPrompt, showInstallBanner, handleInstallPwa, dismissInstallBanner } = pwaHook;

  // ── Pull-to-refresh ──
  const { pullY, refreshing, PULL_THRESHOLD } = usePullToRefresh();

  // ── Document title ──
  useEffect(() => { document.title = gymMeta?.name || "Sendly"; }, [gymMeta?.name]);

  // ═══════════════════════════════════════════════════
  // ROUTE & ASCENT HANDLERS
  // ═══════════════════════════════════════════════════

  const toast = useToast();

  // Undo-toast dismissal: the ascent undo toast should vanish as soon as the
  // user moves on (opens another route or switches tabs) — it's only meant
  // for taking back the ascent the user JUST logged.
  const ascentToastIdRef = useRef(null);
  useEffect(() => {
    if (ascentToastIdRef.current != null) {
      toast.dismiss(ascentToastIdRef.current);
      ascentToastIdRef.current = null;
    }
  }, [selRoute?.id, tab, toast]);

  // The undo toast is created inside `logClimb`, but undoing has to call the
  // removal callbacks defined AFTER it. Refs break the cycle without reordering
  // the callbacks or dropping them out of the dependency arrays. Declared here
  // (empty) rather than next to their assignment, so nothing in this hook is
  // named before it exists.
  const removeClimbRef = useRef(null);
  const removeRouteClimbsRef = useRef(null);

  // ── Climb logging ────────────────────────────────────────────────────────
  // A climb is TWO things now. The `ascents` doc (`{uid}_{routeId}`) records
  // that the route has ever been sent — it carries the points, the flash flag
  // and the uniqueness guarantee the doc id gives us for free. The daily climb
  // log (`climbLogs/{uid}_{YYYY-MM-DD}`) records every climb EVENT, including
  // repeats, and is what volume / streak / occupancy read.
  //
  // A first send writes both IN ONE BATCH. That atomicity is the whole reason
  // the two-collection design is safe: there is no window where one exists
  // without the other, so the projection can never drift from the event log.
  // See docs/plans/repeat-ascents.md.

  /** Current climbs map for today, from the live single-doc listener. Memoized
   *  so `logClimb`'s identity doesn't churn on every render when there is no
   *  log doc yet (a fresh `{}` literal would be a new dependency each time). */
  const todayClimbs = useMemo(() => todayClimbLog?.climbs || {}, [todayClimbLog]);
  // The send / repeat half of today's map. The double-tap guard runs per KIND:
  // a "Próbáltam" followed at once by a "Send!" is two deliberate taps, so the
  // project-day `attempt` entry must not arm the send guard (and vice versa).
  const todayClimbEntries = useMemo(() => {
    const out = {};
    for (const [cid, entry] of Object.entries(todayClimbs)) if (isClimbEntry(entry)) out[cid] = entry;
    return out;
  }, [todayClimbs]);
  // Read by the undo toasts, which run seconds after the callback that created
  // them — by then the closure's `todayClimbLog` is stale.
  const todayClimbLogRef = useRef(todayClimbLog);
  todayClimbLogRef.current = todayClimbLog;

  /**
   * Log one climb on a route. Decides send vs repeat from the existing ascent,
   * so every caller (RouteModal, quick-log) gets the same semantics.
   *
   * The route modal's log block adds the optional extras of a SEND
   * (docs/spec/11-map-routes.md, "What each tap writes"):
   * - `attempts` — null from the log area's Send (unknown until the "?" is answered);
   * - `gradeVote` — "easier" / "fair" / "harder" or null;
   * - `projectAttempts` — the sum of the route's project days. A send from a
   *   project stores the whole count on the ascent (`projectAttempts + N`, N
   *   defaulting to 1), so the ascent says how many tries the send took.
   * A flash is one try by definition, so it stores `attempts: 1`; it is refused
   * (logged as a plain send) when the route already has tries — the Flash
   * button is hidden then, this is the backstop for a stale tap.
   *
   * Internal options of `rewriteAsFlash` (the flash correction, D13), which
   * calls this right after deleting the route's send:
   * - `forceFirstSend` — write a first send whatever the local `ascents` state
   *   says (it still holds the just-deleted ascent until the listener catches
   *   up), skip the double-tap guard (the send it replaces was logged moments
   *   ago) and compute `totals` without the route's deleted send / repeats;
   * - `gaEvent: false` — fire no `log` event (the caller fires one `set_flash`).
   *
   * Returns `{ kind, cid, date }`, or `{ blocked: true }` when the tap landed
   * inside the double-tap guard. Callers MUST surface `blocked` — silently
   * swallowing the tap reads as "it didn't register" and invites more tapping.
   */
  const logClimb = useCallback(async (rid, {
    flash = false, silent = false, attempts = null, gradeVote = null, projectAttempts = 0,
    forceFirstSend = false, gaEvent = true,
  } = {}) => {
    if (!authUser) return null;
    const uid = authUser.uid;
    const date = todayStr(gymMeta?.timezone);
    const route = routes.find(r => r.id === rid);
    const already = forceFirstSend ? null : ascents.find(a => a.userId === uid && a.routeId === rid);
    const kind = already ? CLIMB_KIND.REPEAT : CLIMB_KIND.SEND;

    // Double-tap guard. SAY it rather than silently dropping the tap — a silent
    // no-op reads as "the app didn't register that" and invites more tapping.
    // Quick-log passes `silent` and renders the same message in its own bar.
    if (!forceFirstSend && isWithinCooldown(todayClimbEntries, rid, Date.now())) {
      if (!silent) {
        const name = route ? getRouteDisplayName(route, diff, holdC, t) : t('route.ungraded');
        toast.push({ text: t('toast.climbCooldown', { name }), durationMs: 2000 });
      }
      return { blocked: true, kind };
    }

    // The live map belongs to the gym's current day; past midnight it is
    // yesterday's, and the new day starts empty (as in `logAttempt`).
    const liveDay = liveDayClimbs(todayClimbLog, date);
    const liveClimbs = forceFirstSend ? mapWithoutRouteSends(liveDay, rid) : liveDay;

    // A repeat carries no tries and no flash; a send's counts are pure logic.
    const { isFlash, entryAttempts, ascentAttempts } = kind === CLIMB_KIND.SEND
      ? sendAttemptCounts({ flash, attempts, projectAttempts, todayAttempts: liveClimbs[attemptEntryId(rid)] ? (Number(liveClimbs[attemptEntryId(rid)].attempts) || 1) : 0 })
      : { isFlash: false, entryAttempts: null, ascentAttempts: null };

    // Client clock, not serverTimestamp: the sentinel resolves to null in the
    // local snapshot, which would blind the cooldown check and the ordering for
    // the first ~200ms. Hour-bucket precision is all any consumer needs.
    const cid = doc(gymCollection("climbLogs")).id;
    const entry = buildClimbEntry({ ...(route || {}), id: rid }, kind, { at: Timestamp.now(), flash: isFlash });
    if (entryAttempts != null) entry.attempts = entryAttempts;

    const logRef = gymDoc("climbLogs", climbLogDocId(uid, date));
    // `totals` is written explicitly from the resulting map. It is denormalized
    // display data, but several consumers read it INSTEAD of walking `climbs`
    // — the admin traffic chart, its live today/yesterday overlay, the nightly
    // adminStats aggregate and the friends feed all do. Leaving it unwritten
    // makes every one of them read a busy evening as zero.
    const nextClimbs = { ...liveClimbs, [cid]: entry };
    const logPatch = {
      userId: uid,
      date,
      updatedAt: serverTimestamp(),
      routeIds: arrayUnion(rid),
      climbs: { [cid]: entry },
      totals: computeClimbTotals(nextClimbs),
    };

    const batch = writeBatch(db);
    if (kind === CLIMB_KIND.SEND) {
      // The route's display fields ride on the ascent (@sendly/shared ascentDoc.js).
      batch.set(gymDoc("ascents", ascentDocId(uid, rid)), buildAscentDoc({
        uid, routeId: rid, route, date,
        createdAt: serverTimestamp(), lastClimbedAt: Timestamp.now(),
        flash: isFlash, attempts: ascentAttempts,
        gradeVote: isGradeOpinion(gradeVote) ? gradeVote : null,
      }));
      logPatch.startedAt = serverTimestamp();
    }
    batch.set(logRef, logPatch, { merge: true });
    try {
      await batch.commit();
    } catch (e) {
      // A rejected batch used to vanish silently: `logClimb` is called from
      // click handlers that ignore the returned promise, so the climb would
      // just never appear and the user would have no idea why. The realistic
      // cause is a stale local `ascents` state making this look like a first
      // send on a route that already has one — the ascents rule only permits
      // `repeatCount` / `lastClimbedAt` on an update, so the WHOLE batch dies.
      console.error("Climb log failed", e);
      toast.push({ text: t('toast.climbFailed'), durationMs: 4000 });
      return { failed: true, kind };
    }

    haptic(kind === CLIMB_KIND.SEND ? "success" : "tap");
    // Both counters are maintained server-side (updateGymStats for sends,
    // updateClimbLogStats for repeats). These are only the optimistic overlays
    // the routes listener reconciles.
    if (kind === CLIMB_KIND.SEND) bumpAscentCount(rid, 1);
    trackMAU(gymId, uid).catch(() => {});
    const bucket = kind === CLIMB_KIND.SEND ? attemptsBucket(ascentAttempts) : null;
    if (gaEvent) logEvent(EVT.ASCENT_TOGGLE, {
      gym_id: gymId,
      action: kind === CLIMB_KIND.SEND ? "log" : "repeat",
      is_flash: isFlash,
      ...(bucket ? { attempts_bucket: bucket } : {}),
    });

    if (!silent) {
      const routeName = route ? getRouteDisplayName(route, diff, holdC, t) : t('route.ungraded');
      if (ascentToastIdRef.current != null) toast.dismiss(ascentToastIdRef.current);
      ascentToastIdRef.current = toast.push({
        text: t(kind === CLIMB_KIND.SEND ? 'toast.sendLogged' : 'toast.repeatLogged', { name: routeName }),
        undoLabel: t('toast.undo'),
        durationMs: 2500,
        onUndo: async () => {
          ascentToastIdRef.current = null;
          try {
            // Undoing a first send must take the ascent doc with it, so route
            // through the same path as deleting the send (the tries stay).
            if (kind === CLIMB_KIND.SEND) await removeRouteClimbsRef.current(rid, { silent: true });
            else await removeClimbRef.current(date, cid);
            logEvent(EVT.ASCENT_TOGGLE, { gym_id: gymId, action: "undo_toast", is_flash: isFlash });
          } catch (e) { console.error("Climb undo failed", e); }
        },
        onExpire: () => { ascentToastIdRef.current = null; },
      });
    }
    return { kind, cid, date };
  }, [authUser, ascents, todayClimbLog, todayClimbEntries, gymId, gymCollection, gymDoc, gymMeta?.timezone, routes, diff, holdC, t, toast, bumpAscentCount]);

  /**
   * The route half of one day's `climbs` map: today's from the live listener,
   * an older day's by one read. Returns `null` when the day has no document.
   */
  const loadDayClimbs = useCallback(async (date) => {
    const live = todayClimbLogRef.current;
    if (live && live.date === date) return live.climbs || {};
    const snap = await getDoc(gymDoc("climbLogs", climbLogDocId(authUser.uid, date)));
    return snap.exists() ? (snap.data().climbs || {}) : null;
  }, [authUser, gymDoc]);

  /**
   * Lower (or drop) one day's project-day entry of a route, in one batch with
   * the ascent's `attempts` when the route was sent meanwhile.
   *
   * `by` is the number of tries to take back; `null` drops the entry. Reaching
   * 0 drops it too — that is "deleting the project day". The ascent keeps at
   * least 1 (the send itself). On the SEND's day the send entry's `attempts`
   * drops by the removed count too (never below 1), in the same update, so the
   * day timeline ("13 próbából") keeps up with the dots. Only the route half of
   * the day document moves (`climbs.*`, `totals`, `routeIds`, `updatedAt`); the
   * spray half and the document itself are never touched. The planning is the
   * pure `planAttemptReduction`.
   */
  const reduceAttemptEntry = useCallback(async (rid, date, by = null) => {
    if (!authUser || !rid || !date) return { skipped: true };
    const uid = authUser.uid;
    routeLogCache.forget(rid);
    const climbs = await loadDayClimbs(date);
    const plan = planAttemptReduction({ climbs, rid, by });
    if (!plan) return { skipped: true };
    const { key, remaining, removed } = plan;

    const patch = { updatedAt: serverTimestamp(), totals: plan.totals };
    if (plan.removeEntry) {
      patch[`climbs.${key}`] = deleteField();
      if (plan.dropRouteId) patch.routeIds = arrayRemove(rid);
    } else {
      patch[`climbs.${key}.attempts`] = remaining;
    }
    if (plan.sendEntry) patch[`climbs.${plan.sendEntry.cid}.attempts`] = plan.sendEntry.attempts;

    const batch = writeBatch(db);
    batch.update(gymDoc("climbLogs", climbLogDocId(uid, date)), patch);
    const ascent = ascents.find(a => a.userId === uid && a.routeId === rid);
    if (ascent && typeof ascent.attempts === "number" && removed > 0) {
      const lowered = Math.max(1, ascent.attempts - removed);
      if (lowered !== ascent.attempts) batch.update(gymDoc("ascents", ascentDocId(uid, rid)), { attempts: lowered });
    }
    await batch.commit();
    // Again after the write: a per-route answer that came in meanwhile may predate it.
    routeLogCache.forget(rid);
    return { ok: true, removedEntry: remaining <= 0 };
  }, [authUser, ascents, gymDoc, loadDayClimbs, routeLogCache]);

  /**
   * "Próbáltam +N": tries on a route without a send. One `attempt_{routeId}`
   * entry per route and day — a second tap the same day raises its `attempts`
   * (and `lastAt`), it never makes a second entry. Not on a sent route: a sent
   * route cannot be "tried" (its block shows only "again").
   *
   * Writes only the route half of the day document and never `startedAt` (the
   * first send owns that). Counts for MAU like a climb: a project day is an
   * active day (docs/spec/11-map-routes.md, "What each tap writes").
   */
  const logAttempt = useCallback(async (rid, { attempts = 1 } = {}) => {
    if (!authUser || !rid) return null;
    const uid = authUser.uid;
    if (ascents.some(a => a.userId === uid && a.routeId === rid)) return { skipped: true };
    const n = clampAttempts(attempts) ?? 1;
    const date = todayStr(gymMeta?.timezone);
    const route = routes.find(r => r.id === rid);
    const routeName = route ? getRouteDisplayName(route, diff, holdC, t) : t('route.ungraded');
    const key = attemptEntryId(rid);
    // The live map belongs to the gym's current day; past midnight it is
    // yesterday's, and the new day's entry starts fresh.
    const liveClimbs = liveDayClimbs(todayClimbLog, date);
    const existing = liveClimbs[key] || null;
    const nowMs = Date.now();

    // Per-kind double-tap guard: only an earlier TRY of this route arms it.
    if (existing && nowMs - climbAtMillis({ at: existing.lastAt ?? existing.at }) < CLIMB_COOLDOWN_MS) {
      toast.push({ text: t('toast.climbCooldown', { name: routeName }), durationMs: 2000 });
      return { blocked: true, kind: CLIMB_KIND.ATTEMPT };
    }

    const now = Timestamp.now();
    let entryPatch;
    let nextEntry;
    if (existing) {
      // `increment` rather than the local sum, so a tap on a second device the
      // same day adds up instead of overwriting. The identity fields ride
      // along: if that device deleted the entry meanwhile, the merge recreates
      // a whole entry, never one without `routeId` / `kind`.
      entryPatch = { ...attemptEntryIdentity({ ...(route || {}), id: rid }, existing), attempts: increment(n), lastAt: now };
      nextEntry = { ...existing, attempts: (Number(existing.attempts) || 0) + n, lastAt: now };
    } else {
      nextEntry = { ...buildClimbEntry({ ...(route || {}), id: rid }, CLIMB_KIND.ATTEMPT, { at: now }), attempts: n, lastAt: now };
      entryPatch = nextEntry;
    }

    const batch = writeBatch(db);
    batch.set(gymDoc("climbLogs", climbLogDocId(uid, date)), {
      userId: uid,
      date,
      updatedAt: serverTimestamp(),
      routeIds: arrayUnion(rid),
      climbs: { [key]: entryPatch },
      totals: computeClimbTotals({ ...liveClimbs, [key]: nextEntry }),
    }, { merge: true });
    try {
      await batch.commit();
    } catch (e) {
      console.error("Attempt log failed", e);
      toast.push({ text: t('toast.climbFailed'), durationMs: 4000 });
      return { failed: true, kind: CLIMB_KIND.ATTEMPT };
    }

    haptic("tap");
    trackMAU(gymId, uid).catch(() => {});
    logEvent(EVT.ASCENT_TOGGLE, { gym_id: gymId, action: "attempt", is_flash: false });

    if (ascentToastIdRef.current != null) toast.dismiss(ascentToastIdRef.current);
    ascentToastIdRef.current = toast.push({
      text: t('toast.attemptLogged', { count: n, name: routeName, today: nextEntry.attempts }),
      undoLabel: t('toast.undo'),
      durationMs: 2500,
      onUndo: async () => {
        ascentToastIdRef.current = null;
        try {
          const res = await reduceAttemptEntry(rid, date, n);
          if (res?.ok) logEvent(EVT.ASCENT_TOGGLE, { gym_id: gymId, action: "attempt_undo", is_flash: false });
        } catch (e) {
          console.error("Attempt undo failed", e);
          toast.push({ text: t('toast.climbFailed'), durationMs: 4000 });
        }
      },
      onExpire: () => { ascentToastIdRef.current = null; },
    });
    return { kind: CLIMB_KIND.ATTEMPT, date, attempts: nextEntry.attempts };
  }, [authUser, ascents, todayClimbLog, gymId, gymDoc, gymMeta?.timezone, routes, diff, holdC, t, toast, reduceAttemptEntry]);

  /**
   * A real try dot's "Törlés": take `by` tries (default 1) off one day's
   * `attempt_{routeId}` entry — the outward wrapper of `reduceAttemptEntry`,
   * which also lowers the ascent's total and, on the send's day, the send
   * entry's. Errors toast `toast.climbFailed` and resolve `{ failed: true }`.
   */
  const reduceAttempt = useCallback(async (rid, date, by = 1) => {
    try {
      const res = await reduceAttemptEntry(rid, date, Math.max(1, Math.floor(Number(by)) || 1));
      if (!res?.ok) return res;
      haptic("tap");
      logEvent(EVT.ASCENT_TOGGLE, { gym_id: gymId, action: "delete_try", is_flash: false });
      return res;
    } catch (e) {
      console.error("Try delete failed", e);
      toast.push({ text: t('toast.climbFailed'), durationMs: 4000 });
      return { failed: true };
    }
  }, [reduceAttemptEntry, gymId, toast, t]);

  /**
   * The climber's own day documents that hold an entry of the route, as
   * `[{ date, climbs }]` — the input of the `routeLogWrites` planners. One
   * indexed read (the `userId + routeIds` index `removeRouteClimbs` uses too).
   */
  const loadRouteDayDocs = useCallback(async (rid) => {
    const snap = await getDocs(query(
      gymCollection("climbLogs"),
      where("userId", "==", authUser.uid),
      where("routeIds", "array-contains", rid),
    ));
    return snap.docs.map(d => ({ date: d.data().date, climbs: d.data().climbs || {} }));
  }, [authUser, gymCollection]);

  /**
   * "Hányadik próbára sikerült?": the send took `n` tries (clamped 1–999). One
   * batch: the ascent's `attempts` = n, and the send entry's `attempts` = n minus
   * the tries of the earlier days (never below 1), when the send entry is found.
   * A virtual try dot's delete is `setSendAttempts(rid, sendCount − 1)`. No
   * day's try entry moves; the planning is the pure `planSetSendAttempts`.
   */
  const setSendAttempts = useCallback(async (rid, n) => {
    if (!authUser || !rid) return null;
    const uid = authUser.uid;
    if (!ascents.some(a => a.userId === uid && a.routeId === rid)) return { skipped: true };
    routeLogCache.forget(rid);
    try {
      const plan = planSetSendAttempts({ dayDocs: await loadRouteDayDocs(rid), rid, n });
      if (!plan) return { skipped: true };
      const batch = writeBatch(db);
      batch.update(gymDoc("ascents", ascentDocId(uid, rid)), { attempts: plan.ascentAttempts });
      if (plan.sendEntry) {
        batch.update(gymDoc("climbLogs", climbLogDocId(uid, plan.sendEntry.date)), {
          [`climbs.${plan.sendEntry.cid}.attempts`]: plan.sendEntry.attempts,
          updatedAt: serverTimestamp(),
        });
      }
      await batch.commit();
      routeLogCache.forget(rid);
    } catch (e) {
      console.error("Set send attempts failed", e);
      toast.push({ text: t('toast.climbFailed'), durationMs: 4000 });
      return { failed: true };
    }
    haptic("tap");
    logEvent(EVT.ASCENT_TOGGLE, { gym_id: gymId, action: "set_attempts", is_flash: false });
    return { ok: true };
  }, [authUser, ascents, loadRouteDayDocs, gymDoc, gymId, toast, t, routeLogCache]);

  /**
   * The selection bar's "Az egész nap": delete one day of the route's log in ONE
   * batch. On the send's day that is the send's delete (ascent, send entry,
   * every repeat) plus that day's tries — the other days' try entries stay. On
   * any other day it is that day's tries and repeats, and the ascent's total
   * drops by the deleted tries (never below 1). `totals`, `routeIds` and
   * `updatedAt` follow, as on the other paths; the planning is the pure
   * `planRemoveRouteDay`. Only the route half of each day document moves.
   */
  const removeRouteDay = useCallback(async (rid, date) => {
    if (!authUser || !rid || !date) return { skipped: true };
    const uid = authUser.uid;
    const ascent = ascents.find(a => a.userId === uid && a.routeId === rid);
    routeLogCache.forget(rid);
    try {
      const plan = planRemoveRouteDay({
        dayDocs: await loadRouteDayDocs(rid), rid, date,
        hasAscent: !!ascent, ascentAttempts: ascent ? ascent.attempts : null,
      });
      if (!plan) return { skipped: true };
      const batch = writeBatch(db);
      if (plan.deleteAscent) batch.delete(gymDoc("ascents", ascentDocId(uid, rid)));
      else if (plan.ascentAttempts != null) batch.update(gymDoc("ascents", ascentDocId(uid, rid)), { attempts: plan.ascentAttempts });
      for (const op of plan.dayOps) {
        const patch = { updatedAt: serverTimestamp(), totals: op.totals };
        for (const cid of op.deleteCids) patch[`climbs.${cid}`] = deleteField();
        if (op.dropRouteId) patch.routeIds = arrayRemove(rid);
        batch.update(gymDoc("climbLogs", climbLogDocId(uid, op.date)), patch);
      }
      await batch.commit();
      routeLogCache.forget(rid);
      if (plan.deleteAscent) bumpAscentCount(rid, -1);
    } catch (e) {
      console.error("Day delete failed", e);
      toast.push({ text: t('toast.climbFailed'), durationMs: 4000 });
      return { failed: true };
    }
    haptic("tap");
    logEvent(EVT.ASCENT_TOGGLE, { gym_id: gymId, action: "delete_day", is_flash: false });
    return { ok: true };
  }, [authUser, ascents, loadRouteDayDocs, gymDoc, gymId, bumpAscentCount, toast, t, routeLogCache]);

  /**
   * The log area's opinion pills: change the grade opinion of a sent route. Touches only
   * `gradeVote` on the ascent — `repeatCount` and the rest do not move, and the
   * route's `gradeOpinion` follows through the `updateGymStats` trigger.
   */
  const changeGradeVote = useCallback(async (rid, gradeVote) => {
    if (!authUser || !rid) return null;
    const uid = authUser.uid;
    if (!ascents.some(a => a.userId === uid && a.routeId === rid)) return { skipped: true };
    try {
      await updateDoc(gymDoc("ascents", ascentDocId(uid, rid)), { gradeVote: isGradeOpinion(gradeVote) ? gradeVote : null });
    } catch (e) {
      console.error("Grade vote failed", e);
      toast.push({ text: t('toast.climbFailed'), durationMs: 4000 });
      return { failed: true };
    }
    logEvent(EVT.ASCENT_TOGGLE, { gym_id: gymId, action: "change_grade_vote", is_flash: false });
    return { ok: true };
  }, [authUser, ascents, gymDoc, gymId, toast, t]);

  /** Remove ONE climb-log entry. Only meaningful for repeats — a send is bound
   *  to its ascent doc and can only go away via `removeRouteClimbs`. `gaAction`
   *  tells the toast undo / quick log (`repeat_undo`) from the log area's repeat
   *  dot (`delete_repeat`, the spray vocabulary). */
  const removeClimb = useCallback(async (date, cid, { gaAction = "repeat_undo" } = {}) => {
    if (!authUser || !date || !cid) return;
    const uid = authUser.uid;
    haptic("tap");
    const patch = {
      [`climbs.${cid}`]: deleteField(),
      updatedAt: serverTimestamp(),
    };
    // Keep `totals` and `routeIds` in step with the day's map — see the note in
    // `logClimb`. Any day, not only today: the log area deletes a repeat of an
    // older day, whose map costs one read (today's comes from the listener).
    // Only the route half moves; the spray half and the document itself stay.
    const climbs = await loadDayClimbs(date);
    // A spray entry has no routeId and is no business of the route cache.
    const removedRouteId = climbs?.[cid]?.routeId || null;
    const dropFromCache = () => {
      if (!climbs) routeLogCache.forgetAll(); // the day could not be read: the entry's route is unknown
      else if (removedRouteId) routeLogCache.forget(removedRouteId);
    };
    if (climbs) {
      const { totals, dropRouteId } = planClimbRemoval(climbs, cid);
      patch.totals = totals;
      if (dropRouteId) patch.routeIds = arrayRemove(dropRouteId);
    }
    dropFromCache();
    await updateDoc(gymDoc("climbLogs", climbLogDocId(uid, date)), patch);
    dropFromCache();
    logEvent(EVT.ASCENT_TOGGLE, { gym_id: gymId, action: gaAction, is_flash: false });
  }, [authUser, gymDoc, gymId, loadDayClimbs, routeLogCache]);

  /**
   * Remove a route's send: the ascent doc plus the `send` and EVERY `repeat`
   * entry across every day. The project-day `attempt` entries stay by default
   * (`keepAttempts`), so a route with tries becomes a project again — this is
   * "delete the send" of the route modal, the send's undo toast and the quick
   * log's `−` on a send. `keepAttempts: false` is "Log törlése": every entry of
   * the route goes.
   *
   * `routeIds` loses the route only on a day where no entry of it is left. The
   * ascent is deleted only when the climber has one (a project-only log has
   * none, and a delete of a missing doc is refused by the rules).
   *
   * Client-side is acceptable here (the CLAUDE.md rule targets cascades over
   * OTHER users' data): the query is scoped to the caller's own docs, a route
   * spans a handful of days at most, and the whole thing commits as one atomic
   * batch, so it cannot half-apply if the tab closes. Only the route half of
   * each day document moves; the document itself is never deleted.
   */
  const removeRouteClimbs = useCallback(async (rid, { silent = false, keepAttempts = true, gaAction = "undo" } = {}) => {
    if (!authUser) return;
    const uid = authUser.uid;
    routeLogCache.forget(rid);
    const snap = await getDocs(query(
      gymCollection("climbLogs"),
      where("userId", "==", uid),
      where("routeIds", "array-contains", rid),
    ));
    const hadAscent = ascents.some(a => a.userId === uid && a.routeId === rid);

    const batch = writeBatch(db);
    if (hadAscent) batch.delete(gymDoc("ascents", ascentDocId(uid, rid)));
    for (const d of snap.docs) {
      const climbs = d.data().climbs || {};
      const patch = { updatedAt: serverTimestamp() };
      const kept = {};
      let routeLeft = false;
      for (const [cid, entry] of Object.entries(climbs)) {
        if (!entry) continue;
        const ofRoute = entry.routeId === rid;
        if (ofRoute && !(keepAttempts && entry.kind === CLIMB_KIND.ATTEMPT)) {
          patch[`climbs.${cid}`] = deleteField();
        } else {
          kept[cid] = entry;
          if (ofRoute) routeLeft = true;
        }
      }
      if (!routeLeft) patch.routeIds = arrayRemove(rid);
      patch.totals = computeClimbTotals(kept);
      batch.update(d.ref, patch);
    }
    await batch.commit();
    routeLogCache.forget(rid);

    if (!silent) haptic("tap");
    if (hadAscent) bumpAscentCount(rid, -1);
    // `gaAction: null` fires no event — the caller reports the whole action
    // itself (`rewriteAsFlash` fires one `set_flash` instead of a `delete_send`).
    if (gaAction) logEvent(EVT.ASCENT_TOGGLE, { gym_id: gymId, action: gaAction, is_flash: false });
  }, [authUser, ascents, gymCollection, gymDoc, gymId, bumpAscentCount, routeLogCache]);

  /**
   * The flash correction (D13): turn a plain send of TODAY into a flash. It is
   * a delete plus a create of the same `ascents` doc, and Firestore judges each
   * doc of a batch once (stored version to end state), so one batch would read
   * as an update that adds `flash` — which the rules refuse. So: TWO SEQUENTIAL
   * WRITES. First the send is deleted (`removeRouteClimbs`, no GA event of its
   * own), then the flash is created through `logClimb` as a first send (the
   * local `ascents` state still holds the deleted ascent, and the double-tap
   * guard would block a send logged moments ago). ONE `set_flash` event fires
   * after both succeeded.
   *
   * The route modal offers this only when the log holds nothing but the send,
   * so there are no project tries (`projectAttempts: 0`) and the flash is not
   * downgraded to a plain send. If the second write fails the log is empty and
   * the ⚡ button is back — one tap restores the flash. A failed delete changes
   * nothing. Both failures show `toast.climbFailed`.
   */
  const rewriteAsFlash = useCallback(async (rid) => {
    if (!authUser || !rid) return null;
    try {
      await removeRouteClimbs(rid, { silent: true, gaAction: null });
    } catch (e) {
      console.error("Flash rewrite (delete) failed", e);
      toast.push({ text: t('toast.climbFailed'), durationMs: 4000 });
      return { failed: true, step: "delete" };
    }
    // `logClimb` toasts its own failure.
    const res = await logClimb(rid, { flash: true, silent: true, projectAttempts: 0, forceFirstSend: true, gaEvent: false });
    if (!res || res.failed || res.blocked) return { failed: true, step: "create" };
    logEvent(EVT.ASCENT_TOGGLE, { gym_id: gymId, action: "set_flash", is_flash: true });
    return { ok: true };
  }, [authUser, removeRouteClimbs, logClimb, gymId, toast, t]);

  /**
   * Every log entry of one route (send, repeats AND project-day tries), newest
   * first, each with its `date` and its map key (`cid`). The route modal
   * fetches it when the route-log cache cannot vouch for the route, and after
   * every fix — the project state, the Flash rule and the dots come from it.
   * One indexed read of the climber's own docs (the `userId + routeIds` index
   * the delete path relies on). The answer refills the cache for the route,
   * unless a write came in while it was out (the ticket).
   */
  const fetchRouteClimbs = useCallback(async (rid) => {
    if (!authUser || !rid) return [];
    const ticket = routeLogCache.ticket(rid);
    const snap = await getDocs(query(
      gymCollection("climbLogs"),
      where("userId", "==", authUser.uid),
      where("routeIds", "array-contains", rid),
    ));
    const out = groupClimbLogsByRoute(snap.docs.map(d => d.data())).get(rid) || [];
    routeLogCache.store(rid, out, ticket);
    return out;
  }, [authUser, gymCollection, routeLogCache]);

  // Kept current on every render; the toast's onUndo only reads them later.
  removeClimbRef.current = removeClimb;
  removeRouteClimbsRef.current = removeRouteClimbs;

  const handleSaveRoute = useCallback((data) => { setPending(data); setShowBuilder(false); }, []);

  // A tap on the map while a placement is pending: a new route, a route's new
  // position, or a spray wall's spot. x/y arrive already unrotated and rounded
  // from ZoomableMap.handleSvgClick. Placement ends at the tap, not at the
  // server's acknowledgement (commitPlacement explains why).
  const handleMapClickOrReposition = (x, y) => {
    const placing = pending;
    if (!placing || !authUser) return;
    const kind = placing.kind === "sprayWall" ? "sprayWall" : placing._editId ? "reposition" : "route";
    if (kind === "route") {
      const limits = getPlanLimits(gymMeta);
      if (limits.maxRoutes !== null) {
        const activeCount = routes.filter(r => r.active).length;
        if (activeCount >= limits.maxRoutes) {
          toast.push({ text: t("limits.routesReached", { count: activeCount, max: limits.maxRoutes }), durationMs: 5000 });
          setPending(null);
          return;
        }
      }
    }
    const mapId = activeMapId || getMaps(settings, t)[0]?.id || "default";
    return commitPlacement({
      placing,
      endPlacement: () => setPending(null),
      write: () => {
        if (kind === "sprayWall") return updateDoc(gymDoc("sprayWalls", placing.wallId), sprayWallPlacementPatch(mapId, x, y, authUser.uid));
        if (kind === "reposition") return updateDoc(gymDoc("routes", placing._editId), { x, y, lastModifiedBy: authUser.uid });
        return setDoc(gymDoc("routes", "route_" + Date.now()), buildNewRouteDoc({
          x, y, mapId, name: placing.name, difficulty: placing.difficulty, holdColor: placing.holdColor,
          discipline: placing.discipline, setterUid: authUser.uid, setterName: placing.setterName,
          createdAt: todayStr(gymMeta?.timezone),
        }));
      },
      onSaved: () => {
        if (kind === "sprayWall") {
          // Not a D60 MAU action, so no trackMAU.
          toast.push({ text: t("map.sprayWallPlaced", { name: placing.name }), durationMs: 3000 });
          logEvent(EVT.SPRAY_WALL_MANAGE, { gym_id: gymId, action: "place_on_map" });
          // Back into the wall editor, which started the placement.
          openSprayWall(placing.wallId, "edit");
        } else if (kind === "route") {
          // activeRoutes / per-discipline counters are maintained server-side by the
          // `updateRouteBreakdown` trigger (fires on this route create). No fragile
          // client-side fire-and-forget increment.
          trackMAU(gymId, authUser.uid).catch(() => {});
          logEvent(EVT.ADMIN_ROUTE_CREATE, { gym_id: gymId, has_setter: !!placing.setterName });
        }
      },
      // A failed write here is almost always the Firestore Web SDK bug
      // "INTERNAL ASSERTION FAILED: Unexpected state", which corrupts the SDK
      // until the page reloads — so the route never saves. Prompt the user to
      // refresh. We do NOT auto-reload: a silent reload mid-action hides what
      // happened.
      onFailed: (_, e) => {
        console.error(kind === "sprayWall" ? "Spray wall placement failed" : "Route placement/reposition failed", e);
        toast.push({ text: t(kind === "sprayWall" ? "map.sprayWallSaveFailed" : "map.saveFailed"), durationMs: 6000 });
      },
    });
  };

  const handleRepositionRoute = useCallback((routeId) => {
    const route = routes.find(r => r.id === routeId);
    if (route?.mapId) setActiveMapId(route.mapId);
    setTab("map");
    setPending({ _editId: routeId });
    setSelRoute(null);
  }, [routes, setTab]);

  // ═══════════════════════════════════════════════════
  // ── Competition hook (needed before handlePurge) ──
  const compHook = useCompetition(authUser);
  const seriesHook = useSeries();
  // Membership is tri-state (undefined = not known yet) and comes from
  // GymContext's SERVER-CONFIRMED `memberState`, so useEvents never attaches a
  // listen the rules would deny — not even on a first-time visitor's own
  // pending member-doc write — and attaches the instant the server has the doc.
  const eventsHook = useEvents(authUser, memberState === "loading" ? undefined : memberState === "active");

  // Deep-link: /gym/:slug/event/:eventId
  //
  // The full events snapshot already includes unlisted events (they're only
  // filtered out client-side in EventList for the regular list view). So
  // for a direct link we just need to:
  //   1) Switch to the "comp" (Events) tab so the user sees the list
  //   2) Pass `initialEventId` down to EventList so that specific unlisted
  //      event is whitelisted into the visible list and scrolled into view
  const initialEventIdRef = useRef(initialEventId);
  useEffect(() => {
    const eid = initialEventIdRef.current;
    if (!eid || !gymId) return;
    setTab("comp");
  }, [gymId, setTab]);

  // Database purge — server-side via the purgeGymData CF. The old
  // client-side implementation worked from the in-memory `ascents` /
  // `ratings` state, which is user-scoped (own ascents only) and tab-scoped
  // respectively — so a purge silently deleted only the acting admin's own
  // ascents and left every other member's data behind. The CF reads the
  // real collections, deletes route watchers too, and fixes the counters
  // exactly (a full purge zeroes them, healing any accumulated drift).
  const handlePurge = async (type) => {
    if (type === "competitions") {
      // One `deleteCompetitionCascade` call per competition — the cascade
      // itself is server-side now, this loop just drives it. Sequential on
      // purpose: a purge is rare and each call sweeps a whole comp.
      for (const comp of compHook.competitions) {
        await compHook.deleteCompetition(comp.id);
      }
      for (const s of seriesHook.seriesList) {
        await seriesHook.deleteSeries(s.id);
      }
      toast.push({ text: t('admin.purge.competitionsResult', { count: compHook.competitions.length }), durationMs: 5000 });
      return;
    }
    try {
      const res = await callFunction("purgeGymData", { gymId, type });
      const { routes: delRoutes = 0, ascents: delAscents = 0, ratings: delRatings = 0 } = res.data || {};
      invalidateArchivedRoutes?.();
      toast.push({ text: t('admin.purge.result', { routes: delRoutes, ascents: delAscents, ratings: delRatings }), durationMs: 5000 });
    } catch (e) {
      toast.push({ text: t('common.error') + ": " + callableErrorText(e, e), durationMs: 6000 });
    }
  };

  // ═══════════════════════════════════════════════════
  // SETTINGS & GYM META
  // ═══════════════════════════════════════════════════

  const handleSettingsChange = async (partial) => {
    const update = {};
    for (const [k, v] of Object.entries(partial)) {
      update[`settings.${k}`] = v;
    }
    await updateDoc(gymDocRef, { ...update, updatedAt: serverTimestamp() });
    // Determine setting area from the keys being changed
    const keys = Object.keys(partial);
    const area = keys.some(k => k.startsWith("tv")) ? "tv"
      : keys.some(k => k.includes("Difficulty") || k.includes("difficulty")) ? "difficulty"
      : keys.some(k => k.includes("HoldColor") || k.includes("holdColor")) ? "holdColors"
      : keys.some(k => k === "maps" || k === "mapImageUrl") ? "maps"
      : "general";
    logEvent(EVT.ADMIN_SETTINGS_CHANGE, { gym_id: gymId, setting_area: area });
  };

  // Opening-hours edits reshape the occupancy chart (its x-axis range and
  // which ascents count toward each hour). The chart reads a pre-computed
  // `public/occupancy` doc, so a hours change is invisible there until the
  // doc is rebuilt. Auto-trigger a rebuild on save — debounced, because the
  // editor writes field-by-field and a rebuild scans ~12 weeks of ascents.
  // The nightly routine remains the backstop if this best-effort call fails.
  const occupancyRebuildTimerRef = useRef(null);

  const handleGymMetaChange = async (partial) => {
    await updateDoc(gymDocRef, { ...partial, updatedAt: serverTimestamp() });
    // Note: specialHours intentionally excluded — one-off date overrides do
    // not feed the rolling occupancy average, so a rebuild would be a no-op.
    const affectsOccupancy = "openingHours" in partial || "timezone" in partial;
    if (gymId && affectsOccupancy) {
      if (occupancyRebuildTimerRef.current) clearTimeout(occupancyRebuildTimerRef.current);
      occupancyRebuildTimerRef.current = setTimeout(async () => {
        try {
          await callFunction("rebuildGymOccupancy", { gymId });
        } catch { /* best-effort; nightly updateAllGymOccupancy is the backstop */ }
      }, 4000);
    }
  };

  const handleUpload = async (type, file) => {
    if (!gymId) throw new Error("No gym context — cannot upload.");
    // File type validation
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      throw new Error(t('upload.invalidType'));
    }
    // File size validation (logo: 1.5MB, cover/map: 3MB)
    const maxFileSize = type === "logo" ? 1.5 * 1024 * 1024 : 3 * 1024 * 1024;
    if (file.size > maxFileSize) {
      const maxMB = +(maxFileSize / (1024 * 1024)).toFixed(1);
      throw new Error(t('upload.tooLarge', { max: maxMB }));
    }

    const compressOpts = type === "logo"
      ? { maxWidth: 200, quality: 0.8 }
      : type === "cover"
        ? { maxWidth: 1200, quality: 0.75 }
        : { maxWidth: 800, quality: 0.6 };

    const storagePath = type === "logo"
      ? `gyms/${gymId}/logo.webp`
      : type === "cover"
        ? `gyms/${gymId}/cover.webp`
        : null; // map uploads handled separately in AdminPanel

    const { uploadImage } = await import("../utils/storageUtils");
    const url = await uploadImage(storagePath, file, compressOpts);

    if (type === "logo") {
      await handleGymMetaChange({ logoUrl: url });
    } else if (type === "cover") {
      await handleGymMetaChange({ coverImageUrl: url });
    }

    return url;
  };

  // ═══════════════════════════════════════════════════
  // USER PROFILE
  // ═══════════════════════════════════════════════════

  const handleMarkLegendSeen = useCallback(() => {
    if (!authUser?.uid) return;
    updateDoc(doc(db, "users", authUser.uid), { legendSeen: true }).catch(() => {});
  }, [authUser?.uid]);

  // Account deletion, leaving a gym and the data export now live on the
  // global profile page (/profile#settings/…), which calls the shared utils
  // directly — no gym-scoped wrappers needed here.

  // ═══════════════════════════════════════════════════
  // COMPUTED VALUES
  // ═══════════════════════════════════════════════════

  const { can } = usePermissions();

  // ── App-level tab sources (docs/spec/01f-tab-loading.md) ──
  // What a tab used to hold in its own state lives here, so a tab switch loses
  // nothing: the tab only requests and opens what it shows.
  const adminSources = useAdminStatsSources({ gymId, timezone: gymMeta?.timezone });
  const sprayPickerSources = useSprayPickerSources({
    gymId, uid: authUser?.uid || null, canModerate: can("spraywall.moderate") === true, todayClimbLog,
  });
  const compTabSources = useCompTabSources({
    gymId, uid: authUser?.uid || null, competitions: compHook?.competitions, friendUids: acceptedFriendUids,
  });
  // Two capabilities this hook reads more than once. Each is named for the cap
  // it resolves, not for the role that happens to hold it — `canViewRoutesTab`
  // used to be `isSetter`, which was the same word three other surfaces used
  // for three other capabilities.
  const canViewRoutesTab = can('routes.viewTab');
  const isGymAdmin = can('adminPanel.access');

  // User's own header stats (points + ascent count). Reads each ascent's
  // denormalized route-difficulty snapshot when present, falling back to a
  // routes lookup. The fallback only matches active routes (the listener is
  // active-only), but every ascent should have its snapshot after the
  // backfill, so points stay correct even when a route is later archived.
  const stats = useMemo(() => {
    if (!authUser) return null;
    let cnt = 0, pts = 0;
    for (const a of ascents) {
      cnt++;
      const r = resolveAscentRoute(a, routes);
      if (r) pts += diffAll[r.difficulty]?.points || 0;
    }
    return { cnt, pts };
  }, [authUser, ascents, routes, diffAll]);

  const doneSet = useMemo(() => new Set(ascents.filter(a => a.userId === authUser?.uid).map(a => a.routeId)), [ascents, authUser]);

  // ── Competition routes: THREE questions, three sets ──────────────────────
  // The first two used to share one viewer-dependent set, and that is what
  // leaked: for a competitor it narrowed to their own group, so every OTHER
  // group's boulder fell outside it — neither hidden nor recognised as a
  // competition route. It showed up as an ordinary route, free to pre-inspect
  // and free to log a gym ascent on, mid-competition.
  //
  // 1. "Is this a competition route?" — the same answer for everyone. Drives
  //    hiding on the map, the 🏆 badge, and the stats exclusions.
  const allCompRouteIds = useMemo(() => {
    // Respect the gym's `competitions` feature flag — when it's off there is
    // no competition concept on the map at all (no route hiding, no marker,
    // no legend row, no compOnly filter), even if a stale active comp doc
    // still exists in Firestore (the competitions listener is not flag-gated).
    if (!isFeatureEnabled(gymMeta, "competitions")) return null;
    const active = findActiveStandardCompetition(compHook.competitions);
    if (!active) return null;
    return new Set(getAllCompRouteIds(active));
  }, [compHook.competitions, gymMeta]);

  // 2. "Is it on MY scorecard?" — viewer-dependent: a competitor's own group,
  //    the whole competition for anyone else (a judge scores every group).
  //    Drives the 🏆 filter and which route opens an attempt modal.
  const myScorecardRouteIds = useMemo(() => {
    if (!isFeatureEnabled(gymMeta, "competitions")) return null;
    const active = findActiveStandardCompetition(compHook.competitions);
    if (!active) return null;
    const myReg = compHook.myRegistrationMap?.[active.id];
    if (myReg?.role === "competitor" && myReg.groupId && active.settings?.groupRouteIds?.[myReg.groupId]?.length) {
      return new Set(active.settings.groupRouteIds[myReg.groupId]);
    }
    return new Set(active.settings?.routeIds || []);
  }, [compHook.competitions, compHook.myRegistrationMap, gymMeta]);

  // 3. "Have I already sent it?" — the competition half of that answer. A
  //    competition top NEVER writes an `ascents` doc (it pays no gym points
  //    and must stay distinguishable from a gym ascent), so `doneSet` reads
  //    empty for every competition route and the map showed a competitor's
  //    whole scorecard as untouched — the one screen they stand in front of
  //    between attempts. `activeCompMyAttempts` is their own scorecard in the
  //    running competition (rows a judge recorded for them included), so this
  //    set is null for everyone who is not competing.
  const compToppedRouteIds = useMemo(() => {
    const rows = compHook.activeCompMyAttempts;
    if (!rows?.length) return null;
    const ids = rows.filter(a => a.topped).map(a => a.routeId);
    return ids.length ? new Set(ids) : null;
  }, [compHook.activeCompMyAttempts]);

  // The user's relationship to the running competition, as the map and the
  // Routes tab need it: does a competition own the tap on this route, and what
  // may they do with it. `canScore` / `reason` come from the same helper the
  // Competition tab uses, so a route that is scoreable in the list is
  // scoreable here too.
  const compMapContext = useMemo(() => {
    const activeComp = findActiveStandardCompetition(compHook.competitions);
    if (!activeComp || !authUser) return null;
    const myReg = compHook.myRegistrationMap?.[activeComp.id];
    if (!myReg) return null;
    if (myReg.role === "competitor") {
      const mySession = compHook.activeCompSessions.find(s => s.id === myReg.sessionId);
      // A session that has not started keeps its routes hidden — that secrecy
      // is the whole point of hiding them. But once it HAS started they must
      // not vanish again at a break or at the final whistle, because the
      // scorecard is still open then. Keying visibility on `status ===
      // "running"` is what made a route disappear from the map during a pause
      // while the Competition tab still accepted attempts on it.
      if (!mySession || mySession.status === "scheduled") return null;
      const myGroup = compHook.activeCompGroups.find(g => g.id === myReg.groupId);
      const { canScore, reason } = getSelfScoreState(activeComp, myReg, myGroup, mySession);
      return {
        comp: activeComp, myReg, session: mySession, role: "competitor",
        groupMode: myGroup?.mode || null, canScore, reason,
      };
    }
    // Judge: assigned to the session that is live right now (only one
    // running/paused session exists per competition — R-SS-10).
    const liveSession = compHook.activeCompSessions.find(s => s.status === "running" || s.status === "paused");
    if (myReg.role === "judge" && liveSession && myReg.sessionIds?.includes(liveSession.id)) {
      return { comp: activeComp, myReg, session: liveSession, role: "judge" };
    }
    return null;
  }, [compHook.competitions, compHook.activeCompSessions, compHook.activeCompGroups, compHook.myRegistrationMap, authUser]);

  // A competition owns the meaning of a map tap only while its session is
  // actually live. In the post-session window the scorecard is still open (the
  // route stays visible and tappable), but quick-log and the automatic
  // comp-only filter go back to behaving normally.
  const compSessionLive = !!compMapContext
    && (compMapContext.session?.status === "running" || compMapContext.session?.status === "paused");

  // Auto-activate competition routes filter only when a session is actively running
  const compFilterAutoSet = useRef(false);
  useEffect(() => {
    if (compFilterAutoSet.current) return;
    if (compSessionLive) {
      compFilterAutoSet.current = true;
      setFilters(f => ({ ...f, compOnly: true }));
    }
  }, [compSessionLive]);

  // Is a competition route on the map FOR THIS VIEWER? Extracted from the
  // `filteredRoutes` predicate below because a second caller needs the same
  // answer: the "show on map" crosshair, which must not promise a marker the
  // map filter hides. Non-competition routes are always true here — the other
  // visibility rules (active, floor, user filters) stay with their callers.
  //
  // Being entered decides FIRST, ahead of any staff role: an entered setter is
  // a competitor before they are a setter, so they see their own scorecard's
  // routes once their session starts — and nothing before it, same as
  // everybody else. Without that ordering they would either lose the routes
  // they have to score, or get to study the other groups' boulders while
  // entered in the competition.
  const isCompRouteVisible = useCallback((routeId) => {
    if (!allCompRouteIds?.has(routeId)) return true;
    // `compMapContext` exists exactly when the viewer is entered AND inside
    // the window. A judge's scorecard set is the whole competition — they
    // score every group; a competitor's is their own group only.
    if (compMapContext) return !!myScorecardRouteIds?.has(routeId);
    // Staff who are NOT entered keep seeing every competition route: they set
    // and manage them. "Staff" here is `routes.viewTab` (the setter's own tab)
    // or `adminPanel.access` — caps, so an `extraCaps` grant works too.
    return canViewRoutesTab || isGymAdmin;
  }, [allCompRouteIds, compMapContext, myScorecardRouteIds, canViewRoutesTab, isGymAdmin]);

  /** May the "show on map" crosshair be offered for this route right now?
   *  Needs a placed, active route that this viewer's map actually renders.
   *  `!!r.active`, not `r.active !== false` — `filteredRoutes` below keys on
   *  truthiness, so a legacy route with no `active` field is off the map and
   *  must not be offered a jump to a marker that is not there. */
  const canShowRouteOnMap = useCallback((r) => (
    !!r && !!r.active && r.x != null && r.y != null && isCompRouteVisible(r.id)
  ), [isCompRouteVisible]);

  const filteredRoutes = useMemo(() => routes.filter(r => {
    if (!r.active) return false;
    const rMapId = r.mapId || "default";
    if (rMapId !== currentMapId) return false;
    // Discipline filter
    if (disciplineFilter !== "all" && (r.discipline || "boulder") !== disciplineFilter) return false;
    // Competition routes on the map — see isCompRouteVisible above.
    if (!isCompRouteVisible(r.id)) return false;
    if (filters.difficulty.length) {
      const rd = r.difficulty || "_ungraded";
      if (!filters.difficulty.includes(rd)) return false;
    }
    // "Climbed" spans both halves — a gym ascent and a competition top —
    // exactly like the tick the marker and the list row draw. Splitting them
    // would file a boulder the competitor just topped under "Not climbed".
    if (filters.status !== "all") {
      const climbed = doneSet.has(r.id) || !!compToppedRouteIds?.has(r.id);
      if (filters.status === "done" && !climbed) return false;
      if (filters.status === "todo" && climbed) return false;
    }
    if (filters.compOnly && myScorecardRouteIds && !myScorecardRouteIds.has(r.id)) return false;
    if (mapSearch) {
      const q = mapSearch.toLowerCase();
      const displayName = getRouteDisplayName(r, diff, holdC, t).toLowerCase();
      if (!displayName.includes(q) && !(r.name || "").toLowerCase().includes(q) && !(r.setterName || "").toLowerCase().includes(q)) return false;
    }
    return true;
  }), [routes, filters, doneSet, compToppedRouteIds, currentMapId, mapSearch, isCompRouteVisible, myScorecardRouteIds, disciplineFilter, diff, holdC, t]);

  // ── Quick log ────────────────────────────────────────────────────────────
  // Blocked while another mode already owns the meaning of a map tap: placing a
  // route or a spray wall (`pending`) or being inside a running competition session, where the
  // tap opens the attempt modal instead.
  const quickLog = useQuickLog({
    gymId,
    todayKey,
    blocked: !!pending || compSessionLive,
    logClimb,
    removeClimb,
    removeRouteClimbs,
  });

  // ── Archive mode ─────────────────────────────────────────────────────────
  // The setter counterpart of quick-log: one tap archives, fullscreen map only.
  // Same blocking rule — a tap can only mean one thing at a time — and the two
  // modes disarm each other where they are toggled (MapTab).
  const archiveMode = useArchiveMode({
    canArchive: can('routes.archive'),
    // Leaving the map tab disarms it: the banner and the toggle both live on
    // the map, so anywhere else the mode would be on with nothing saying so.
    blocked: !!pending || !!compMapContext || tab !== "map",
    setRouteActive,
    toast,
    t,
  });

  // Per-route average rating — read straight from the denormalized
  // route.ratingSum / route.ratingCount fields (maintained by the
  // ratingStats CF, delivered live by the routes listener). This means the
  // map markers and the ⭐ sort never need the full gym ratings collection,
  // and the average still updates live the moment anyone rates a route.
  const avgRatingMap = useMemo(() => {
    const m = new Map();
    for (const r of routes) {
      const count = Number(r.ratingCount) || 0;
      if (count > 0) m.set(r.id, (Number(r.ratingSum) || 0) / count);
    }
    return m;
  }, [routes]);

  const sortedRouteList = useMemo(
    () => sortRoutes(filteredRoutes, mapSort, { difficulty: diff, holdColors: holdC, t, avgRatingMap, ascentCounts }),
    [filteredRoutes, mapSort, avgRatingMap, ascentCounts, diff, holdC, t]
  );

  const planLimits = getPlanLimits(gymMeta);

  // Check if user should see a badge on the competition/events tab
  const compTabBadge = useMemo(() => {
    if (!authUser) return false;
    const canManageComps = can('competitions.manage');
    const canManageEvents = can('events.manage');

    // An active challenge shows an awareness dot for EVERYONE (incl. admins/
    // managers): a challenge has no registration to nudge, so the dot just
    // signals "a challenge is live". Standard comps keep the personal
    // register-nudge below (from which managers are excluded).
    if (compHook.competitions.some(c => c.type === "challenge" && c.status === "active")) return true;

    // Admins/managers don't get the registration-nudge badge for their own domain.
    if (!canManageComps) {
      // Standard competitions only. A challenge has no registration concept,
      // and an EXTERNAL competition cannot be entered in the app at all — both
      // would light the dot forever, since nothing the user can do turns it off.
      const activeComps = activeStandardCompetitions(compHook.competitions);
      if (activeComps.some(c => !compHook.myRegistrationMap?.[c.id])) return true;
    }
    if (!canManageEvents) {
      const activeEvents = (eventsHook.events || []).filter(e =>
        e.status === "published" && e.visibility !== "unlisted"
      );
      if (activeEvents.some(e => !eventsHook.myParticipantMap?.[e.id] && !eventsHook.myWaitlistMap?.[e.id])) return true;
    }
    return false;
  }, [authUser, can, compHook.competitions, compHook.myRegistrationMap, eventsHook.events, eventsHook.myParticipantMap, eventsHook.myWaitlistMap]);

  // If the user is on the competition/events tab but both feature flags are
  // disabled, bounce them back to the map tab. Otherwise they'd see an empty
  // "feature not available" screen stuck on a tab that no longer exists in
  // the nav bar.
  useEffect(() => {
    if (tab !== "comp") return;
    if (!gymMeta) return;
    const compsOn = isFeatureEnabled(gymMeta, "competitions");
    const eventsOn = isFeatureEnabled(gymMeta, "events");
    if (!compsOn && !eventsOn) bounceToMap();
  }, [tab, gymMeta, bounceToMap]);

  // The Spraywall tab's bounce-back, the same idea split in two (plan §9.1):
  // add-on off or not an active member → map at once; add-on on but the wall
  // list (or the member doc) not delivered yet → keep the tab, which shows a
  // loader, so a `#spraywall…` deep link survives a cold start. A forced
  // bounce does not ask the navigation guard: the tab is gone either way.
  const canManageSprayWall = can('spraywall.manage');
  const sprayWallTabShown = sprayWallTabVisible({
    addonOn: sprayWallAddonOn, memberState, walls: sprayWalls, canManage: canManageSprayWall,
  });
  useEffect(() => {
    if (tab !== SPRAY_WALL_TAB) return;
    const decision = sprayWallTabDecision({
      gymLoaded: !!gymMeta, addonOn: sprayWallAddonOn, memberState,
      wallsLoaded: sprayWallsLoaded, walls: sprayWalls, canManage: canManageSprayWall,
    });
    if (decision === "bounce") bounceToMap();
  }, [tab, gymMeta, sprayWallAddonOn, memberState, sprayWallsLoaded, sprayWalls, canManageSprayWall, bounceToMap]);

  const tabs = useMemo(() => {
    const compsOn = isFeatureEnabled(gymMeta, "competitions");
    const eventsOn = isFeatureEnabled(gymMeta, "events");
    const showCompTab = compsOn || eventsOn;
    // Tab label: if events feature is on, show "Események"; otherwise keep "Verseny"
    const compTabLabel = eventsOn ? t('events.tab') : t('tab.comp');
    const compTabLabelShort = eventsOn ? t('events.tab.short') : t('tab.comp.short');
    return [
      { key: "map", label: t('tab.map'), labelShort: t('tab.map.short'), icon: "🗺️" },
      // The one SVG icon in the bar (D90). The coloured form, so it sits
      // among the colour emoji of the other tabs; like them it keeps its
      // colours on the active tab. An emoji renders ~20px wide in the bar's
      // 16px icon slot and its line box is 1.2em tall; a bare 16px svg was
      // visibly smaller and 3px shorter, lifting the label above its
      // neighbours. The panel fills only 17 of the icon's 24 units, so the
      // svg is 24px to draw a ~17px panel, in a box exactly one emoji line
      // tall (the svg's empty margin overflows it harmlessly).
      ...(sprayWallTabShown ? [{ key: SPRAY_WALL_TAB, label: t('tab.spraywall'), labelShort: t('tab.spraywall.short'), icon: createElement("span", { style: SPRAY_TAB_ICON_BOX }, createElement(IconSprayWallColor, { size: 24 })) }] : []),
      { key: "stats", label: t('stats.title'), labelShort: t('stats.title.short'), icon: "📊" },
      ...(showCompTab ? [{ key: "comp", label: compTabLabel, labelShort: compTabLabelShort, icon: "🏆", badge: compTabBadge }] : []),
      ...(can('routes.viewTab') ? [{ key: "routes", label: t('tab.routes'), labelShort: t('tab.routes.short'), icon: "📋" }] : []),
      ...(can('stats.viewSetterTab') && isFeatureEnabled(gymMeta, "setterStats") ? [{ key: "setters", label: t('tab.setters'), labelShort: t('tab.setters.short'), icon: "🔧" }] : []),
      ...(can('adminPanel.access') ? [{ key: "admin", label: t('tab.admin'), labelShort: t('tab.admin.short'), icon: "👑" }] : []),
    ];
  }, [t, gymMeta, compTabBadge, can, sprayWallTabShown]);

  // Utak / Építők / Admin named by the URL but not in the viewer's bar — a member's
  // `#admin` link, or a capability revoked while its tab is open — go back to the
  // map once the capabilities are known (utils/tabAccess.js). The content of those
  // tabs renders only behind the same check, so staying meant an empty page.
  useEffect(() => {
    if (!gymMeta) return;
    const decision = capGatedTabDecision({
      tab, visibleTabs: tabs.map(x => x.key), memberState, userLoaded: !!userData,
    });
    if (decision === "bounce") bounceToMap();
  }, [tab, tabs, gymMeta, memberState, userData, bounceToMap]);

  // ═══════════════════════════════════════════════════
  // RETURN
  // ═══════════════════════════════════════════════════

  return {
    // True while useScrollMemory is driving the page itself — the collapsing
    // header has to sit those frames out.
    scrollRestoring,
    // Translation
    t, lang,
    // Navigation
    navigate,
    // Auth
    authUser, authLoading, userData, seedName, handleLogin, handleAppleLogin, handleEmailLink, handleLogout,
    emailInput, setEmailInput, passwordInput, setPasswordInput,
    emailSent, setEmailSent,
    authError, setAuthError, needsEmailConfirm, handleConfirmEmail,
    isPasswordAuthEnabled, handlePasswordSignIn, handlePasswordSignUp,
    // Gym
    gymId, gymMeta, settings, accent, diff, holdC, th,
    planLimits, tabs,
    // Data
    users, members, routes, ascents, allAscents, friendships,
    ascentCounts, fetchAllAscents,
    rankingAscents, fetchRankingAscents, rankingStatus,
    routeAscents, fetchAscentsForRoutes,
    routeRatings, fetchRatingsForRoutes,
    archivedRoutes, archivedRoutesHasMore, fetchArchivedRoutes, loadMoreArchivedRoutes, fetchAllArchivedRoutes, fetchRoutesArchivedSince,
    // Spray walls (empty without the add-on); loaded = first snapshot arrived
    sprayWalls, sprayWallsLoaded,
    // Own member + ban flag (set by useGymData from the live member doc)
    ownMember, isBanned,
    // Friendship derived
    acceptedFriendUids, pendingIncoming, friendsFilter, setFriendsFilter,
    // Roles / Permissions
    isGymAdmin, can,
    // Stats
    stats,
    // UI state
    tab, setTab, selRoute, setSelRoute, selRouteEditMode, setSelRouteEditMode, leaderboardDiscipline, setLeaderboardDiscipline,
    showBuilder, setShowBuilder, pending, setPending,
    showGymInfo, setShowGymInfo,
    // Lets a tab with unsaved work veto leaving it (returns the unregister)
    registerNavigationGuard,
    // Map state
    filters, setFilters, setterSearch, setSetterSearch,
    mapSearch, setMapSearch, mapSort, setMapSort,
    showMapFilters, setShowMapFilters,
    activeMapId, setActiveMapId, maps, currentMapId, currentMap,
    // `compRouteIds` keeps its name for every consumer that asks "is this a
    // competition route" (badges, stats exclusions, show-on-map suppression);
    // `myScorecardRouteIds` is the narrower, viewer-specific set.
    filteredRoutes, sortedRouteList, doneSet, compToppedRouteIds, activeFilterCount, toggleDiff, hasUngradedRoutes, hasUnsetFieldRoutes, compRouteIds: allCompRouteIds, myScorecardRouteIds, compMapContext, canShowRouteOnMap, avgRatingMap,
    disciplineFilter, setDisciplineFilter,
    // Pull-to-refresh
    pullY, refreshing, PULL_THRESHOLD,
    // PWA
    isStandalone, isIOSStandalone, isIOS,
    // True only when the PWA was installed from a gym-specific URL (not the main sendly domain).
    // We detect this by checking if the initial page load was on a /gym/ path — stored in sessionStorage.
    isGymPwa: isStandalone && isFeatureEnabled(gymMeta, "gymPwa") && sessionStore.get("sendly-pwa-scope") === "gym",
    installPrompt, showInstallBanner, handleInstallPwa, dismissInstallBanner,
    // Handlers
    logClimb, logAttempt, reduceAttempt, setSendAttempts, removeRouteDay, rewriteAsFlash, changeGradeVote, removeClimb, removeRouteClimbs, fetchRouteClimbs, routeLogCache, todayClimbLog, fetchClimbLogs, myGymStats,
    quickLog, archiveMode,
    handleRate,
    openSprayWall,
    handleSaveRoute, handleMapClickOrReposition, handleEditRoute, handleDeleteRoute, handleRepositionRoute,
    handleRole, handleKick, handleBan, handleUnban, handleReportName,
    handleBulkArchive, handleBulkDelete, handlePurge,
    handleSettingsChange, handleGymMetaChange, handleUpload,
    handleMarkLegendSeen,
    handleSendFriendRequestByUid,
    // App-level tab sources (docs/spec/01f-tab-loading.md)
    adminSources, sprayPickerSources, compTabSources,
    // The always-on data has arrived; the gym-timezone day (warm-up, prefetch)
    dataLoaded, todayKey,
    // Competition
    compHook,
    // Series
    seriesHook,
    // Events
    eventsHook,
  };
}
